import React, { useEffect, useMemo, useState } from "react";
import { api, toastError } from "../../lib/api";
import { InteractionVoidDialog } from "./InteractionVoidDialog";
import { useAuth } from "../../context/AuthContext";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { Ban, Circle, Clock, Phone, Mail, Users, MessageSquare, StickyNote, PhoneCall, ArrowRight, ArrowLeft } from "lucide-react";

export type Interaction = {
  id: string;
  kind_code: string;
  contact_id: string | null;
  opportunity_id: string | null;
  organization_id: string | null;
  actor_user_id: string;
  performed_by_user_id: string;
  actor_name?: string | null;
  performed_by_name?: string | null;
  subject: string | null;
  notes: string | null;
  occurred_at: string;
  direction: "inbound" | "outbound" | null;
  duration_minutes: number | null;
  origin: "user" | "import";
  historical_attribution: boolean;
  voided: boolean;
  voided_at: string | null;
  voided_by_user_id: string | null;
  void_reason: string | null;
  version: number;
  created_at: string;
  updated_at: string;
};

const KIND_ICON: Record<string, any> = {
  call: PhoneCall, email: Mail, meeting: Users,
  note: StickyNote, voicemail: Phone, sms: MessageSquare,
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

type Props = {
  scope:
    | { contact_id: string }
    | { opportunity_id: string }
    | { organization_id: string };
  reloadKey: number;
  onChanged: () => void;
  /** Extra events to merge before rendering — used by OpportunityDetail
   *  to fold stage transitions into the same timeline. */
  extra?: MergedEvent[];
  emptyLabel?: string;
};

export type MergedEvent =
  | { kind: "interaction"; occurred_at: string; data: Interaction }
  | { kind: "stage_transition"; occurred_at: string; data: {
        id: string; from_canonical: string | null; to_canonical: string;
        reason: string | null; actor_user_id: string;
    } };

export function InteractionTimeline({ scope, reloadKey, onChanged, extra, emptyLabel }: Props) {
  const [items, setItems] = useState<Interaction[] | null>(null);
  const [voidTarget, setVoidTarget] = useState<Interaction | null>(null);
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const canVoidAny = canAtLeast(effectiveRole, "manager_trainer");
  const canVoidOwn = canAtLeast(effectiveRole, "representative");

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("include_voided", "true");
    Object.entries(scope).forEach(([k, v]) => v && params.set(k, String(v)));
    api
      .get<{ items: Interaction[]; total: number }>(`/crm/interactions?${params}`)
      .then((r) => setItems(r.data.items))
      .catch(toastError);
  }, [scope, reloadKey]);

  const events: MergedEvent[] = useMemo(() => {
    const ints = (items || []).map<MergedEvent>((i) => ({
      kind: "interaction",
      occurred_at: i.occurred_at,
      data: i,
    }));
    const merged = [...ints, ...(extra || [])];
    merged.sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : -1));
    return merged;
  }, [items, extra]);

  if (items === null) {
    return (
      <p className="px-4 py-6 text-sm text-slate-500" data-testid="interaction-timeline-loading">
        Loading activity…
      </p>
    );
  }
  if (events.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-slate-500" data-testid="interaction-timeline-empty">
        {emptyLabel || "No activity yet."}
      </p>
    );
  }

  const userId = user?.id;

  return (
    <>
      <ol className="divide-y divide-slate-100" data-testid="interaction-timeline">
        {events.map((ev) => (
          <li key={`${ev.kind}-${(ev.data as any).id}`} className="px-4 py-3 flex items-start gap-3"
              data-testid={`timeline-event-${ev.kind}-${(ev.data as any).id.slice(0, 8)}`}>
            {ev.kind === "interaction" ? (
              <InteractionRow
                i={ev.data as Interaction}
                canVoid={
                  !ev.data.voided &&
                  (canVoidAny || (canVoidOwn && (ev.data as Interaction).actor_user_id === userId))
                }
                onVoidClick={() => setVoidTarget(ev.data as Interaction)}
              />
            ) : (
              <StageTransitionRow t={ev.data as any} />
            )}
          </li>
        ))}
      </ol>
      {voidTarget && (
        <InteractionVoidDialog
          open={!!voidTarget}
          onOpenChange={(o) => !o && setVoidTarget(null)}
          interactionId={voidTarget.id}
          version={voidTarget.version}
          onSuccess={onChanged}
        />
      )}
    </>
  );
}

function InteractionRow({ i, canVoid, onVoidClick }: {
  i: Interaction; canVoid: boolean; onVoidClick: () => void;
}) {
  const Icon = KIND_ICON[i.kind_code] || Circle;
  return (
    <>
      <span
        className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-slate-600 ${
          i.voided ? "bg-slate-100" : "bg-emerald-50"
        }`}
        aria-hidden
      >
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div className={`min-w-0 flex-1 ${i.voided ? "opacity-60" : ""}`}>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium capitalize" data-testid={`timeline-interaction-kind-${i.id.slice(0, 8)}`}>
            {i.kind_code}
          </span>
          {i.direction && (
            <span className="text-xs text-slate-500 inline-flex items-center gap-0.5"
                  data-testid={`timeline-interaction-direction-${i.id.slice(0, 8)}`}>
              {i.direction === "inbound" ? (
                <><ArrowLeft className="w-3 h-3" /> inbound</>
              ) : (
                <><ArrowRight className="w-3 h-3" /> outbound</>
              )}
            </span>
          )}
          {i.duration_minutes != null && (
            <span className="text-xs text-slate-500 inline-flex items-center gap-0.5">
              <Clock className="w-3 h-3" /> {i.duration_minutes}m
            </span>
          )}
          {i.voided && (
            <span
              className="ml-1 text-[10px] uppercase tracking-widest text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded"
              data-testid={`timeline-interaction-voided-${i.id.slice(0, 8)}`}
            >
              Voided
            </span>
          )}
          {i.historical_attribution && (
            <span className="ml-1 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
              Imported
            </span>
          )}
        </div>
        {i.subject && (
          <div className="text-sm text-slate-800 truncate" data-testid={`timeline-interaction-subject-${i.id.slice(0, 8)}`}>
            {i.subject}
          </div>
        )}
        {i.notes && (
          <div className="text-xs text-slate-600 whitespace-pre-wrap mt-0.5"
               data-testid={`timeline-interaction-notes-${i.id.slice(0, 8)}`}>
            {i.notes}
          </div>
        )}
        {i.voided && i.void_reason && (
          <div className="text-xs text-rose-700 mt-1" data-testid={`timeline-interaction-void-reason-${i.id.slice(0, 8)}`}>
            Voided: {i.void_reason}
          </div>
        )}
        <div className="text-xs text-slate-500 mt-1">
          Logged by {i.performed_by_name || i.actor_name || "Unknown user"} · {formatDateTime(i.occurred_at)}
        </div>
      </div>
      {canVoid && (
        <button
          className="text-xs text-slate-500 hover:text-rose-700 inline-flex items-center gap-1 shrink-0 focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
          onClick={onVoidClick}
          data-testid={`timeline-interaction-void-${i.id.slice(0, 8)}`}
          aria-label={`Void interaction ${i.subject || i.kind_code}`}
        >
          <Ban className="w-3.5 h-3.5" /> Void
        </button>
      )}
    </>
  );
}

function StageTransitionRow({ t }: {
  t: { id: string; from_canonical: string | null; to_canonical: string; reason: string | null; };
}) {
  return (
    <>
      <span className="mt-1 shrink-0 w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center" aria-hidden>
        <ArrowRight className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm">
          <span className="font-medium">Stage change</span>
        </div>
        <div className="text-xs text-slate-600 mt-0.5">
          {t.from_canonical ? (
            <>
              <span className="text-slate-500">{t.from_canonical.replace(/_/g, " ")}</span>
              <span className="text-slate-400"> → </span>
            </>
          ) : (
            <span className="text-slate-500">created at </span>
          )}
          <span className="font-medium">{t.to_canonical.replace(/_/g, " ")}</span>
        </div>
        {t.reason && (
          <div className="text-xs text-slate-500 mt-0.5">Reason: {t.reason}</div>
        )}
      </div>
    </>
  );
}
